import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Search, MoreVertical } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import DataTable from "../ui/DataTable";
import { fieldService } from "../../services/fieldService";
import { useAuth } from "../../context/AuthContext";
import ConfirmDialog from "../ui/ConfirmDialog";
import toast from "react-hot-toast";
import { getStoredClientId } from "../../utils/tokenUtils";

const FieldsTab = ({ clientId: propClientId }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);

  // Use prop clientId if provided, otherwise get from user
  const effectiveClientId =
    propClientId || getStoredClientId() || user?.client_id;
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredFields, setFilteredFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    if (effectiveClientId) {
      loadFields();
      loadProjects();
    }
  }, [effectiveClientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProjects = async () => {
    try {
      const { projectService } = await import("../../services/projectService");
      const response = await projectService.getProjects({
        client_id: effectiveClientId,
        page: 1,
        limit: 1000,
        is_active: true,
        is_deleted: false,
      });
      if (response.success && response.data?.projects) {
        setProjects(response.data.projects);
      }
    } catch (error) {
      console.error("Error loading projects:", error);
    }
  };

  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = fields.filter((field) =>
        field.name.toLowerCase().includes(searchQuery.toLowerCase()),
      );
      setFilteredFields(filtered);
    } else {
      setFilteredFields(fields);
    }
  }, [searchQuery, fields]);

  const loadFields = async () => {
    try {
      setLoading(true);
      const response = await fieldService.getFields({
        client_id: effectiveClientId,
      });
      if (response.success && response.data?.fields) {
        // For each field, if it's dropdown/multiselect, load datasets
        const fieldsWithDatasets = await Promise.all(
          response.data.fields.map(async (field) => {
            if (field.type === "dropdown" || field.type === "multiselect") {
              try {
                const { datasetService } =
                  await import("../../services/datasetService");
                const datasetsResponse = await datasetService.getDatasets(
                  field.field_id,
                );
                if (
                  datasetsResponse.success &&
                  datasetsResponse.data?.datasets
                ) {
                  // Load values for each dataset
                  const datasetsWithValues = await Promise.all(
                    datasetsResponse.data.datasets.map(async (dataset) => {
                      try {
                        const datasetResponse =
                          await datasetService.getDatasetById(
                            dataset.dataset_id,
                          );
                        if (
                          datasetResponse.success &&
                          datasetResponse.data?.dataset
                        ) {
                          return datasetResponse.data.dataset;
                        }
                        return dataset;
                      } catch (e) {
                        console.error("Error loading dataset values:", e);
                        return dataset;
                      }
                    }),
                  );
                  return { ...field, datasets: datasetsWithValues };
                }
              } catch (e) {
                console.error("Error loading datasets:", e);
              }
            }
            return field;
          }),
        );
        setFields(fieldsWithDatasets);
        setFilteredFields(fieldsWithDatasets);
      } else {
        setFields([]);
        setFilteredFields([]);
      }
    } catch (error) {
      console.error("Error loading fields:", error);
      toast.error("Failed to load fields");
      setFields([]);
      setFilteredFields([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (field) => {
    setSelectedField(field);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!selectedField) return;

    try {
      setDeleteLoading(true);
      await fieldService.deleteField(selectedField.field_id);
      toast.success("Field deleted successfully");
      setShowDeleteDialog(false);
      setSelectedField(null);
      loadFields();
    } catch (error) {
      console.error("Error deleting field:", error);
      toast.error(error.response?.data?.message || "Failed to delete field");
    } finally {
      setDeleteLoading(false);
    }
  };

  const columns = [
    {
      key: "fields",
      header: "FIELDS",
      render: (_, row) => (
        <div>
          <div className="font-medium">{row.name || "-"}</div>
          {getFieldDisplayValue(row) && (
            <div className="text-sm text-gray-500 mt-1">
              {getFieldDisplayValue(row)}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "projects",
      header: "PROJECTS",
      render: (_, row) => (
        <span className="text-sm text-gray-600">{getProjectsDisplay(row)}</span>
      ),
    },
     {
      key: "fieldType",
      header: "System/Custom",
      render: (value, row) => (
       row.is_system_defined?"System":"Custom"
      ),
    },
    {
      key: "type",
      header: "TYPE",
      render: (value, row) => (
        <Badge variant={row.is_system_defined ? "default" : "secondary"}>
          {row.is_system_defined ? "System" : "Custom"}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      render: (_, row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => {
                const clientId = effectiveClientId || user?.client_id;
                if (clientId && row) {
                  navigate(
                    `/fields/edit/${row.field_id}?client_id=${clientId}`,
                  );
                } else {
                  toast("Please select a client first");
                }
              }}
            >
              <Pencil className="w-4 h-4 mr-2" />
              Configure Field
            </DropdownMenuItem>
            {!row?.is_system_defined && (
              <DropdownMenuItem
                onClick={() => handleDelete(row)}
                className="text-red-600"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Field
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const getFieldDisplayValue = (field) => {
    // For dropdown/multiselect, show dataset values if available
    if (
      (field.type === "dropdown" || field.type === "multiselect") &&
      field.datasets &&
      field.datasets.length > 0
    ) {
      const allValues = [];
      field.datasets.forEach((dataset) => {
        if (dataset.values && dataset.values.length > 0) {
          dataset.values.forEach((value) => {
            allValues.push(value.value_name);
          });
        }
      });
      if (allValues.length > 0) {
        return allValues.join(", ");
      }
    }
    // For other types, show default_value or options if available
    if (field.default_value) {
      return String(field.default_value);
    }
    if (field.options) {
      try {
        const opts =
          typeof field.options === "string"
            ? JSON.parse(field.options)
            : field.options;
        if (Array.isArray(opts)) {
          return opts.join(", ");
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    return "";
  };

  const getProjectsDisplay = (field) => {
    if (field.linked_projects_count === 0) {
      return "0 Projects";
    }
    if (field.linked_projects && Array.isArray(field.linked_projects)) {
      const count = field.linked_projects.length;
      if (count === projects.length) {
        return "All Projects";
      }
      return `${count} ${count === 1 ? "Project" : "Projects"}`;
    }
    return `${field.linked_projects_count || 0} Projects`;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-gray-600 text-sm mt-1">
            Create and manage custom fields for test cases
          </p>
        </div>
        <Button
          onClick={() => {
            const clientId = effectiveClientId || user?.client_id;
            if (clientId) {
              navigate(`/fields/create?client_id=${clientId}`);
            } else {
              toast("Please select a client first");
            }
          }}
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Field
        </Button>
      </div>

      {/* Search */}
      <div className="max-w-md">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 w-4 h-4 text-text-tertiary" />
          <Input
            placeholder="Search field by name"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>
      <div className="card overflow-hidden">
        <DataTable
          data={filteredFields}
          columns={columns}
          loading={loading}
          emptyMessage="No fields found. Create your first custom field to get started."
        />
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedField(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Field"
        message={`Are you sure you want to delete "${selectedField?.name}"? This may result in test cases getting delinked with this field.`}
        confirmText="Delete Field"
        confirmVariant="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default FieldsTab;
